import React from 'react';
import { AbstractButton } from '../AbstractButton';
import { Icon } from '../Icon';

export interface ChipDeleteButtonProps {
  /** Called when the user clicks the × button. Click does not bubble. */
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Required accessible label, e.g. `Remove George Metakos`. */
  'aria-label': string;
  disabled?: boolean;
}

/**
 * Standalone × button slot for `InteractiveChip` composed mode. Renders a
 * real `<button>` with click `stopPropagation` so the chip's own `onClick`
 * never fires for the same gesture.
 *
 * @example
 * ```tsx
 * <InteractiveChip variant="primary" onClick={openProfile}>
 *   <ChipAvatar src={user.avatarUrl} />
 *   <ChipLabel>{user.name}</ChipLabel>
 *   <ChipDeleteButton
 *     onClick={removeMember}
 *     aria-label={`Remove ${user.name}`}
 *   />
 * </InteractiveChip>
 * ```
 */
export const ChipDeleteButton = React.forwardRef<HTMLButtonElement, ChipDeleteButtonProps>(
  function ChipDeleteButton({ onClick, 'aria-label': ariaLabel, disabled = false }, ref) {
    return (
      <AbstractButton
        ref={ref}
        aria-label={ariaLabel}
        // Use `aria-disabled` instead of the native `disabled` attribute. The
        // native one applies MUI's `Mui-disabled` styling which includes
        // `pointer-events: none` — that would route the mouse click straight
        // through to the parent chip's `onClick` (the button can't catch it
        // to run `stopPropagation`). With `aria-disabled` the button stays
        // interactive: the click hits this handler, `stopPropagation` fires,
        // and we no-op the consumer's `onClick` ourselves.
        aria-disabled={disabled || undefined}
        onClick={e => {
          e.stopPropagation();
          if (!disabled) {
            onClick(e);
          }
        }}
        opacity={disabled ? 0.4 : 1}
        sx={{ color: 'inherit', cursor: disabled ? 'not-allowed' : undefined }}
      >
        <Icon type="close" aria-hidden />
      </AbstractButton>
    );
  },
);
